import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconLock } from '@sorare/core/src/atoms/icons/system/iconLock.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import type { DailyActionTaskReward_anyRewardConfig } from './__generated__/index.graphql.ts';

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  20% {
    transform: scale(0.9);
  }
  40% {
    transform: scale(1);
  }
`;

const borderPulseAnimation = keyframes`
  30% {
    transform: scale(1);
    opacity: 1;

  }
  80% {
    transform: scale(1.5);
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
`;

const Root = styled(Vertical)`
  &.claimable,
  &.claiming {
    animation: ${pulseAnimation} 2s ease-in-out infinite;
  }
`;

const RewardBlock = styled.div`
  padding: var(--half-unit);
  border-radius: var(--half-unit);
  background: var(--c-nd-100-opaque);
  position: relative;
  .claimable &,
  .claiming & {
    cursor: pointer;
    border: 1px solid var(--c-nd-150);
    background: var(--c-blue-400);
    box-shadow: 0 0 4px 0 var(--c-blue-400);
    &:after {
      content: '';
      position: absolute;
      inset: 0;
      border: 1px solid var(--c-blue-400);
      border-radius: var(--half-unit);
      animation: ${borderPulseAnimation} 2s ease-in-out infinite;
    }
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
  & > * {
    height: 14px;
  }
`;

type Props = {
  state: 'claimed' | 'locked';
  anyRewardConfig: DailyActionTaskReward_anyRewardConfig;
};

const Icon = ({
  state,
}: {
  state: 'claimed' | 'claiming' | 'claimable' | 'locked';
}) => {
  if (state === 'claimed') {
    return <FontAwesomeIcon icon={iconCheck} size="xs" />;
  }
  if (state === 'locked') {
    return <FontAwesomeIcon icon={iconLock} size="xs" />;
  }
  return null;
};
export const DailyActionTaskReward = ({ state, anyRewardConfig }: Props) => {
  const { formatMessage } = useIntl();
  if (!isType(anyRewardConfig, 'InGameCurrencyRewardConfig')) {
    return null;
  }
  return (
    <Root center gap={0.5} className={state}>
      <RewardBlock>
        <RewardConfigImage
          anyRewardConfig={anyRewardConfig}
          alt={
            state === 'locked'
              ? formatMessage(glossary.locked)
              : formatMessage(glossary.claimed)
          }
        />
        <IconWrapper>
          <Icon state={state} />
        </IconWrapper>
      </RewardBlock>
      {state !== 'claimed' && <LabelS bold>x{anyRewardConfig.amount}</LabelS>}
    </Root>
  );
};

DailyActionTaskReward.fragments = {
  anyRewardConfig: gql`
    fragment DailyActionTaskReward_anyRewardConfig on AnyRewardConfigInterface {
      id
      ... on InGameCurrencyRewardConfig {
        id
        amount
      }
      ...RewardConfigImage_anyRewardConfigInterface
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<DailyActionTaskReward_anyRewardConfig>,
};
